import * as tus from "tus-js-client";
import { authClient } from "@utils/authClient";
import { serverEndpoints } from "@utils/serverEndpoints";
import { Video } from "types";

interface UploadVideoMetadata {
  name: string;
  org_id?: string;
  extension?: string;
  is_public?: boolean;
}

interface UploadVideoParams {
  file: File;
  metadata: UploadVideoMetadata;
  onProgress?: (percent: number) => void;
}

// Cloudflare Stream requires tus chunks to be a multiple of 256 KiB; 50 MiB is
// the recommended size. https://developers.cloudflare.com/stream/uploading-videos/upload-video-file/
const CHUNK_SIZE = 52428800;

/**
 * Uploads a video using Cloudflare Stream's direct creator upload flow:
 *
 *   1. Ask our server for a one-time upload URL (small JSON request).
 *   2. Upload the file straight to Cloudflare via tus — this bypasses the
 *      Worker's request-body size limit that used to block large videos.
 *   3. Finalize: our server records the video row and returns it.
 */
export async function uploadVideoToStream({
  file,
  metadata,
  onProgress,
}: UploadVideoParams): Promise<Video> {
  const accessToken = await authClient.getAccessToken();
  const authHeader = { Authorization: `Bearer ${accessToken}` };
  const is_public = metadata.is_public ?? false;

  // 1) Request a one-time direct-creator upload URL from our server.
  const createResponse = await fetch(serverEndpoints.uploadVideo, {
    method: "POST",
    headers: { ...authHeader, "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      name: metadata.name,
      org_id: metadata.org_id,
      is_public,
      uploadLength: file.size,
    }),
  });

  if (!createResponse.ok) throw new Error("Upload failed");
  const { uploadURL, uid } = (await createResponse.json()) as {
    uploadURL: string;
    uid: string;
  };

  // 2) Upload the file directly to Cloudflare Stream.
  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl: uploadURL,
      chunkSize: CHUNK_SIZE,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      onError: (error) => reject(error),
      onProgress: (bytesUploaded, bytesTotal) => {
        if (bytesTotal > 0) {
          onProgress?.(Math.round((bytesUploaded / bytesTotal) * 100));
        }
      },
      onSuccess: () => resolve(),
    });

    upload.start();
  });

  // 3) Finalize: persist the video row and return it.
  const finalizeResponse = await fetch(serverEndpoints.finalizeVideoUpload, {
    method: "POST",
    headers: { ...authHeader, "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      uid,
      name: metadata.name,
      org_id: metadata.org_id,
      is_public,
      extension: metadata.extension,
    }),
  });

  if (!finalizeResponse.ok) throw new Error("Upload failed");
  return finalizeResponse.json();
}
